// EP-135 Tier 1 — Meetings list. PageHeader primaryAction for admin+,
// staff sees the list but no Create button (per §8b). Filters: meeting_type,
// status. Sorted DESC by meeting_at by default.
import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CalendarDays, Plus, Loader2 } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Button, Select, EmptyState, Skeleton, Pagination, Badge } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useMeetings } from '@/hooks/useGovernance'
import { useNotificationStore, useSelectedPropertyStore } from '@/stores'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { MeetingFormModal } from './components/MeetingFormModal'
import type { MeetingType, MeetingStatus } from '@/types/governance'

export function MeetingListPage() {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const addToast = useNotificationStore((s) => s.addToast)
  const formatDateTime = useDateTimeFormatter()

  const [page, setPage] = useState(1)
  const [meetingType, setMeetingType] = useState<MeetingType | ''>('')
  const [status, setStatus] = useState<MeetingStatus | ''>('')
  const [createOpen, setCreateOpen] = useState(false)
  // MON-18 C: follows the header's selected building; no selection lists
  // meetings across every building (unchanged).
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)

  const { data, isLoading, isFetching, error } = useMeetings({
    page,
    meetingType: meetingType || undefined,
    status: status || undefined,
    propertyId: selectedPropertyId ?? undefined,
  })

  const canCreate = user?.role === 'admin' || user?.role === 'owner'

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[ { label: t('meetings.title') } ]} />

        <PageHeader
          title={t('meetings.title')}
          description={t('meetings.description')}
          isRefreshing={isFetching && !isLoading}
          primaryAction={
            canCreate
              ? {
                  key: 'new-meeting',
                  label: t('meetings.actions.new_meeting'),
                  icon: Plus,
                  onClick: () => setCreateOpen(true),
                  testId: 'btn-new-meeting',
                }
              : undefined
          }
        />

        <Card bodyClassName="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <Select
              label={t('meetings.labels.meeting_type')}
              value={meetingType}
              onChange={(e) => { setMeetingType(e.target.value as MeetingType | ''); setPage(1) }}
              options={[
                { value: '', label: t('meetings.filters.all_types') },
                { value: 'annual_general_meeting', label: t('meetings.types.annual_general_meeting') },
                { value: 'extraordinary', label: t('meetings.types.extraordinary') },
                { value: 'committee', label: t('meetings.types.committee') },
              ]}
              data-testid="filter-meeting-type"
            />
            <Select
              label={t('meetings.labels.status')}
              value={status}
              onChange={(e) => { setStatus(e.target.value as MeetingStatus | ''); setPage(1) }}
              options={[
                { value: '', label: t('meetings.filters.all_statuses') },
                { value: 'draft', label: t('meetings.statuses.draft') },
                { value: 'noticed', label: t('meetings.statuses.noticed') },
                { value: 'in_progress', label: t('meetings.statuses.in_progress') },
                { value: 'closed', label: t('meetings.statuses.closed') },
                { value: 'certified', label: t('meetings.statuses.certified') },
              ]}
              data-testid="filter-status"
            />
            {isFetching && !isLoading && (
              <div className="flex items-center text-sm text-base-content/60" aria-label={tc('labels.refreshing')}>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                {tc('labels.refreshing')}
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[ ...Array(5) ].map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              title={tc('errors.something_went_wrong')}
              description={tc('errors.something_went_wrong')}
              action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
            />
          ) : !data?.data || data.data.length === 0 ? (
            <EmptyState
              icon={<CalendarDays className="w-12 h-12" />}
              title={t('meetings.empty.no_meetings')}
              description={t('meetings.empty.no_meetings_description')}
              action={
                canCreate ? (
                  <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
                    {t('meetings.actions.new_meeting')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('meetings.labels.title')}</th>
                    <th>{t('meetings.labels.meeting_type')}</th>
                    <th>{t('meetings.labels.meeting_at')}</th>
                    <th>{t('meetings.labels.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((m) => (
                    <tr key={m.id} className="hover:bg-base-200">
                      <td>
                        <Link to={`/meetings/${m.id}`} className="link link-hover font-medium" data-testid={`meeting-${m.id}`}>
                          {m.title}
                        </Link>
                      </td>
                      <td>{t(`meetings.types.${m.meeting_type}`)}</td>
                      <td>{formatDateTime(m.meeting_at)}</td>
                      <td>
                        <Badge variant="count">{t(`meetings.statuses.${m.status}`)}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {data?.meta && data.meta.total_pages > 1 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      <MeetingFormModal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false)
          addToast({ type: 'success', message: t('meetings.messages.created') })
        }}
      />
    </>
  )
}
